import React from 'react';
import { ToggleGroup } from '@base-ui/react/toggle-group';
import { Toggle } from '@base-ui/react/toggle';
import styled from '@emotion/styled';
import theme from '@/theme';

const ToggleGroupField = <T extends string>({
  options,
  value,
  onValueChange,
  disabled,
}: {
  options: { value: T; label: string; disabled?: boolean }[];
  value: T;
  onValueChange: (value: T) => void;
  disabled?: boolean;
}) => (
  <StyledGroup
    value={[value]}
    onValueChange={(values) => {
      const selected = options.find(
        (option) => option.value !== value && values.includes(option.value)
      );
      if (selected) {
        onValueChange(selected.value);
      }
    }}
  >
    {options.map((option) => (
      <StyledToggle
        key={option.value}
        value={option.value}
        data-selected={option.value === value}
        disabled={disabled || option.disabled}
      >
        {option.label}
      </StyledToggle>
    ))}
  </StyledGroup>
);

export default ToggleGroupField;

const StyledGroup = styled(ToggleGroup)`
  display: flex;
  flex-direction: row;
`;

const StyledToggle = styled(Toggle)`
  padding: ${theme.sizes.scale[1]}px ${theme.sizes.scale[3]}px;
  margin-left: -1px; /* offset double borders between toggles */

  background-color: ${theme.colors.field};
  border-color: ${theme.colors.fieldBorder};
  border-width: ${theme.buttons.root.borderWidth};
  border-style: ${theme.buttons.root.borderStyle};
  cursor: pointer;

  font-weight: ${theme.text.body1a.fontWeight};
  font-size: ${theme.text.body1a.fontSize};
  line-height: ${theme.text.body1a.lineHeight};
  font-family: ${theme.text.body1a.fontFamily};

  &:first-of-type {
    margin-left: 0;
    border-top-left-radius: ${theme.radii.circle}px;
    border-bottom-left-radius: ${theme.radii.circle}px;
  }

  &:last-of-type {
    border-top-right-radius: ${theme.radii.circle}px;
    border-bottom-right-radius: ${theme.radii.circle}px;
  }

  &:hover:not([data-disabled]):not([data-selected='true']) {
    background-color: ${theme.colors.fieldHover};
  }

  &[data-disabled] {
    color: ${theme.colors.fieldBorder};
    cursor: default;
  }

  &[data-selected='true'] {
    background-color: ${theme.colors.primary};
    border-color: ${theme.colors.primary};
    color: ${theme.colors.white};
  }
`;
